import React from "react";
import { useCMWorklistContext } from "../context/CMWorklist.context";
import { Col, Row, Badge } from "react-bootstrap";
import { getPatientCardMarks } from "../../../constants";
import Mark from "../../Mark";
import { setManageVisitOpenedFromPageTitle, setManageVisitPatient } from "../../../../../../actions/manageVisitActions";
import { useDispatch } from "react-redux";
import { _calculateAge, formatDateModern } from "../../../../../../utils/util";
import PatientRecordBoldField from "../fields/PatientRecordBoldField";
import RiskColumn from "./RiskColumn";

const componentName = "FullNameColumn";

const FullNameColumn = ({ patient, idx }) => {
  const { activeRole } = useCMWorklistContext();
  const { role } = activeRole;

  const columnComponents = {
    'Clinical Coordinators': FullNameColumnClinical,
    'Hospital Liaisons': FullNameColumnHospitalLiaisons,
    'Community Health Workers': FullNameCHW,
    'Chaplains': FullNameChaplain,
  };

  const ColumnComponent = columnComponents[role] || FullNameColumnDefault;

  return (
    <Col id={`${componentName}-${idx}`} style={{ minWidth: 350 }}>
      <Row>
        <Col className="d-flex flex-column">
          <ColumnComponent patient={patient} idx={idx} role={role} />
        </Col>
      </Row>
    </Col>
  );
};

const PatientHeader = ({ patient, idx, dispatch }) => {
  const patientCardMarks = getPatientCardMarks(patient);

  return (
    <div className="d-flex align-items-center">
      <a
        id={`${componentName}-fullName-${idx}`}
        data-bs-toggle="offcanvas"
        data-bs-target="#patientDetailsModal"
        aria-controls="patientDetailsModal"
        className="fw-bold patientName align-middle me-3"
        onClick={() => {
          dispatch(setManageVisitPatient(patient));
          dispatch(setManageVisitOpenedFromPageTitle('CMWorklist'));
        }}
      >
        <span className="forced-capital fs-5 d-inline-block">{patient.fullName}</span>
      </a>
      {patientCardMarks
        .filter(item => item.shouldBeRendered)
        .map((item, index) => <Mark key={index} text={item.text} customClassName={item.customClassName} />)
      }
      {patient?.carePathStepDto?.stepState === 'SNOOZED' && (
        <Mark text="Snoozed patient" customClassName="fas fa-snooze text-muted me-1" />
      )}
      <RiskColumn patient={patient} idx={idx} />
    </div>
  );
};

const PatientInfo = ({ patient, showGender = false }) => (
  <div className="d-flex">
    <PatientRecordBoldField sourceName={`${componentName}-id`} label="ID:" value={patient?.memberId} />
    <BlankSpace />
    <PatientRecordBoldField sourceName={`${componentName}-dob`} label="DOB:" value={formatDateModern(patient?.dateOfBirth)} />
    {patient?.carePathStepDto?.stepState !== 'SNOOZED' && (
      <>
        <Badge
          bg="light"
          text="dark"
          className="border p-1 ms-2"
          style={{ fontSize: '8px', lineHeight: '11px', height: 'fit-content' }}
        >
          {_calculateAge(patient?.dateOfBirth)}
        </Badge>
        {showGender && <Badge bg="light" text="dark" className="border p-1 ms-2">{patient?.gender}</Badge>}
      </>
    )}
  </div>
);

const PatientContact = ({ patient, showAddress = true, role }) => (
  <div className="d-flex flex-column text-muted font-xs">
    {(role !== 'Admin Role' && role !== 'Hub Coordinator') &&
      <PatientRecordBoldField sourceName={`${componentName}-phone`} label="Patient Phone:" value={patient.primaryPhone} />}
    {showAddress && patient?.carePathStepDto?.stepState !== 'SNOOZED' && (
      <PatientRecordBoldField
        sourceName={`${componentName}-address`}
        label="Address:"
        value={patient?.fullAddress || patient?.address || patient?.address2}
      />
    )}
    {(role !== 'Admin Role' && role !== 'Hub Coordinator') &&
      <PatientRecordBoldField sourceName={`${componentName}-adtPhone`} label="ADT Phone:" value={patient?.adtPhone} />}
  </div>
);

const BlankSpace = () => <span>&nbsp;&nbsp;</span>;

const FullNameColumnDefault = (props) => {
  const dispatch = useDispatch();
  return (
    <Col id={`${componentName}-${props.idx}`} className="px-0">
      <PatientHeader {...props} dispatch={dispatch} />
      <PatientInfo {...props} />
      <PatientContact {...props} />
    </Col>
  );
};

const FullNameChaplain = (props) => <FullNameColumnDefault {...props} showGender={true} />;

const FullNameCHW = (props) => <FullNameColumnDefault {...props} showGender={true} />;

const FullNameColumnClinical = (props) => <FullNameColumnDefault {...props} showGender={true} />;

const FullNameColumnHospitalLiaisons = (props) => {
  const dispatch = useDispatch();
  return (
    <Col id={`${componentName}-hospital-liaisons-${props.idx}`} className="px-0">
      <PatientHeader {...props} dispatch={dispatch} />
      <PatientInfo {...props} showGender={true} />
      {props.patient?.carePathStepDto?.stepState !== 'SNOOZED' ? (
        <PatientContact {...props} />
      ) : (
        <PatientRecordBoldField sourceName={`${componentName}-phone`} label="Phone:" value={props.patient.primaryPhone} />
      )}
    </Col>
  );
};

export default FullNameColumn;